import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";

const AutoLogoutContext = createContext({
  countdown: 600,
  minutes: 10,
  seconds: 0,
  resetTimer: () => {},
  showModal: false,
  setShowModal: () => {},
});

export const AutoLogoutProvider = ({
  timeoutMinutes = 10,
  warningSeconds = 60, // Shows warning modal when <= 60 seconds remain
  onLogout,
  isEnabled = true,
  children,
}) => {
  // Ensure timeout is a valid positive number in seconds
  const timeoutSeconds = Math.max(Number(timeoutMinutes) || 10, 1) * 60;
  const activeCountdownThreshold = Math.max(warningSeconds, 120); // only trigger 1s state updates when <= 120s

  const [countdown, setCountdown] = useState(timeoutSeconds);
  const [showModal, setShowModal] = useState(false);

  const lastActivityRef = useRef(Date.now());
  const onLogoutRef = useRef(onLogout);
  const timeoutSecondsRef = useRef(timeoutSeconds);
  const showModalRef = useRef(showModal);
  const countdownRef = useRef(timeoutSeconds);

  // Keep references current without re-triggering effects
  useEffect(() => {
    onLogoutRef.current = onLogout;
  }, [onLogout]);

  useEffect(() => {
    showModalRef.current = showModal;
  }, [showModal]);

  // When timeoutMinutes changes, update ref and reset timer
  useEffect(() => {
    timeoutSecondsRef.current = timeoutSeconds;
    lastActivityRef.current = Date.now();
    countdownRef.current = timeoutSeconds;
    setCountdown(timeoutSeconds);
    setShowModal(false);
  }, [timeoutSeconds]);

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    countdownRef.current = timeoutSecondsRef.current;
    setCountdown(timeoutSecondsRef.current);
    setShowModal(false);
  }, []);

  useEffect(() => {
    if (!isEnabled) {
      setShowModal(false);
      return;
    }

    // Check timer status based on real elapsed time
    const checkTimer = () => {
      const elapsed = Math.floor((Date.now() - lastActivityRef.current) / 1000);
      const remaining = Math.max(0, timeoutSecondsRef.current - elapsed);
      countdownRef.current = remaining;

      // Only trigger React state update if we are within the visible countdown threshold (e.g. <= 120s)
      // or if transitioning from active countdown back to quiet mode
      if (remaining <= activeCountdownThreshold) {
        setCountdown(remaining);
      }

      // Warning threshold check (handles browser throttling cleanly)
      if (remaining <= warningSeconds && remaining > 0) {
        if (!showModalRef.current) {
          setShowModal(true);
        }
      }

      // Logout threshold
      if (remaining <= 0) {
        setShowModal(false);
        if (typeof onLogoutRef.current === "function") {
          onLogoutRef.current();
        }
      }
    };

    // User activity listener throttled to at most once every 1s
    let lastHandled = 0;
    const handleActivity = (e) => {
      // If warning modal is already showing, ignore casual mouse movements
      // so the user can read the dialog and click "Stay Logged In"
      if (showModalRef.current && e.type === "mousemove") {
        return;
      }

      const now = Date.now();
      if (now - lastHandled > 1000) {
        lastHandled = now;
        lastActivityRef.current = now;
        if (showModalRef.current) {
          setShowModal(false);
        }
        // If we were previously in the active countdown window, reset state back
        if (countdownRef.current <= activeCountdownThreshold) {
          setCountdown(timeoutSecondsRef.current);
        }
      }
    };

    const events = ["keydown", "click", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, handleActivity, { passive: true }));

    // Re-check immediately when tab becomes visible or focused
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        checkTimer();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", handleVisibility);

    // Periodic 1-second interval
    const interval = setInterval(checkTimer, 1000);

    return () => {
      events.forEach((e) => window.removeEventListener(e, handleActivity));
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", handleVisibility);
      clearInterval(interval);
    };
  }, [isEnabled, warningSeconds, activeCountdownThreshold]);

  const value = useMemo(
    () => ({
      countdown,
      minutes: Math.floor(countdown / 60),
      seconds: countdown % 60,
      resetTimer,
      showModal,
      setShowModal,
    }),
    [countdown, resetTimer, showModal]
  );

  return (
    <AutoLogoutContext.Provider value={value}>
      {children}
    </AutoLogoutContext.Provider>
  );
};

export const useAutoLogout = () => useContext(AutoLogoutContext);
